import React, { useCallback, useEffect, useState, useMemo } from 'react'
import {
  Download,
  Plus,
  X,
  Upload,
  ArrowLeft,
  StickyNote,
  MapPin,
  Info,
  PenTool,
  ChevronDown,
  CreditCard,
  Building2,
  Receipt,
  FileText,
  ExternalLink,
} from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import api from '@/services/api'
import { formatDateForDisplay } from '@/components/account-settings/DateFormatUtility'
import { formatTimestampWithDuration, toCaps } from './Utils'
import ContractTimeline from '@/components/contracts/ContractTimeline'
import DocumentsSection from '@/components/contracts/DocumentsSection'
import ContractDocuments from '@/components/contracts/ContractDocuments'
import {
  getContractTimeline,
  getContractDocuments,
  downloadContractDocument,
  ContractTimelineItem,
  ContractDocument,
} from '@/services/contractsService'
import DocuSignSigner from './DocSign'
import { notesApi, NoteListItem } from '@/services/notesApi'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  capitalize,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Box,
} from '@mui/material'
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi'
import { ChatDialog } from '@/chat/components/ChatDialog'
import { useAppSelector } from '@/store'
import { Chat } from '@mui/icons-material'
import dayjs from 'dayjs'

const ContractDetailsPage = () => {
  const navigate = useNavigate()
  const { contractId } = useParams<{ contractId: string }>()
  const [activeTab, setActiveTab] = useState('All Sessions')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [contractDetails, setContractDetails] = useState<any>({})
  const [sessionDetails, setSessionDetails] = useState<any[]>([])

  const [nextDate, setNextDate] = useState('')
  const [duration, setDuration] = useState(0)

  // Timeline, Documents, and Contract Documents state
  const [timeline, setTimeline] = useState<ContractTimelineItem[]>([])
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [contractDocuments, setContractDocuments] = useState<
    ContractDocument[]
  >([])
  const [contractDocumentsLoading, setContractDocumentsLoading] =
    useState(false)
  const [documents, setDocuments] = useState<
    Array<{
      id: string
      type: string
      createdOn: string
      status?: 'pending' | 'authorized' | 'rejected'
      downloadUrl?: string
    }>
  >([])
  const [documentsLoading, setDocumentsLoading] = useState(false)
  const [signingModalOpen, setSigningModalOpen] = useState(false)
  const [signingLoading, setSigningLoading] = useState(false)

  // Dialog states
  const [addSessionDialogOpen, setAddSessionDialogOpen] = useState(false)
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [selectedSessionForAction, setSelectedSessionForAction] =
    useState<any>(null)

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  })

  // Chat state
  const [chatDialogOpen, setChatDialogOpen] = useState(false)
  const [chatProvider, setChatProvider] = useState<any>(null)

  // Get current user (patient)
  const { user: authUser } = useAppSelector(state => state.auth)

  // Calendar states for Add Session/Reschedule
  const today = useMemo(() => new Date(), [])
  const [monthOffset, setMonthOffset] = useState(0)
  const monthStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + monthOffset, 1),
    [today, monthOffset]
  )
  const daysInMonth = useMemo(
    () =>
      new Date(
        monthStart.getFullYear(),
        monthStart.getMonth() + 1,
        0
      ).getDate(),
    [monthStart]
  )
  const monthDates = useMemo(() => {
    const arr: (Date | null)[] = []
    const firstDayOfWeek = monthStart.getDay()
    for (let i = 0; i < firstDayOfWeek; i++) {
      arr.push(null)
    }
    for (let i = 1; i <= daysInMonth; i++) {
      arr.push(new Date(monthStart.getFullYear(), monthStart.getMonth(), i))
    }
    return arr
  }, [monthStart, daysInMonth])

  // Session selection states for Add Session
  const [selectedDate, setSelectedDate] = useState<Date | null>(today)
  const [slotsForDate, setSlotsForDate] = useState<any[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [selectedSlots, setSelectedSlots] = useState<
    Record<string, { slotId: string; startTime: string; endTime: string }>
  >({})
  const [paymentMethod, setPaymentMethod] = useState<
    'insurance' | 'out_of_pocket' | ''
  >('')

  // Reschedule dialog states (single slot selection)
  const [rescheduleSelectedDate, setRescheduleSelectedDate] =
    useState<Date | null>(today)
  const [rescheduleSlotsForDate, setRescheduleSlotsForDate] = useState<any[]>(
    []
  )
  const [loadingRescheduleSlots, setLoadingRescheduleSlots] = useState(false)
  const [selectedRescheduleSlot, setSelectedRescheduleSlot] =
    useState<any>(null)

  const [cancelReason, setCancelReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Notes state
  const [sessionNotes, setSessionNotes] = useState<
    Record<string, NoteListItem[]>
  >({})
  const [loadingNotes, setLoadingNotes] = useState<Record<string, boolean>>({})
  const [openNotesDropdown, setOpenNotesDropdown] = useState<string | null>(
    null
  )


  // API function to fetch job for editing
  const fetchContract = async (contractId: string) => {
    try {
      setLoading(true)
      setError(null)

      const res = await api.get(`/contracts/detail/${contractId}`)
      const contract = res?.data?.data
      setContractDetails(contract)
      setNextDate(contract?.next_date)
      setDuration(contract?.service_id?.session_length)

      const res2 = await api.get(`/contracts/get-session/${contractId}`)
      const sessions = res2?.data?.sessions
      setSessionDetails(sessions)
    } catch (err: any) {
      console.error('Error fetching job:', err)
      setError(
        'Failed to fetch job: ' + (err.response?.data?.message || err.message)
      )
    } finally {
      setLoading(false)
    }
  }


  // Load timeline data
  const loadTimeline = useCallback(async (id: string) => {
    setTimelineLoading(true)
    try {
      const timelineData = await getContractTimeline(id)
      setTimeline(timelineData)
    } catch (apiError) {
      console.error('[ContractDetailsPage] Failed to load timeline', apiError)
    } finally {
      setTimelineLoading(false)
    }
  }, [])

  // Load contract documents
  const loadContractDocuments = useCallback(async (id: string) => {
    setContractDocumentsLoading(true)
    try {
      const documentsResponse = await getContractDocuments(id)
      setContractDocuments(documentsResponse.data || [])
    } catch (apiError) {
      console.error(
        '[ContractDetailsPage] Failed to load contract documents',
        apiError
      )
    } finally {
      setContractDocumentsLoading(false)
    }
  }, [])

  // Handle download contract document
  const handleDownloadContractDocument = useCallback(
    async (documentId: string): Promise<string> => {
      try {
        const signedUrl = await downloadContractDocument(documentId)
        if (signedUrl) {
          // Return the URL so the component can handle the download
          return signedUrl
        }
        throw new Error('No download URL received')
      } catch (error) {
        console.error('Failed to download document:', error)
        throw error
      }
    },
    []
  )

  // Handle download document (for Documents section - SOAP notes, etc.)
  const handleDownloadDocument = useCallback(async (documentId: string) => {
    // TODO: Implement document download when API is available

  }, [])

  // Handle upload document
  const handleUploadDocument = useCallback(() => {
    // TODO: Implement document upload when API is available

  }, [])

  // Load job data if in edit mode
  useEffect(() => {
    if (contractId) {
      fetchContract(contractId)
      loadTimeline(contractId)
      loadContractDocuments(contractId)
      // TODO: Load documents section when API is available
      // loadDocuments(contractId)
    }
  }, [contractId, loadTimeline, loadContractDocuments])

  const downloadContract = async (documentId: string) => {
    try {
      const response = await api.get(`/docusign/download/${documentId}`)

      const signedUrl = response?.data?.signedUrl
      if (!signedUrl) throw new Error('Signed URL missing')

      // Auto-open the PDF in a new tab
      window.open(signedUrl, '_blank')

      return response // blob response
    } catch (error) {
      console.error('Error downloading DocuSign document:', error)
      throw error
    }
  }

  const handleESignClick = () => {
    setSigningModalOpen(true)
  }

  const handleCloseSigningModal = () => {
    setSigningModalOpen(false)
  }

  const handleSigned = async () => {
    // Refresh contract data after signing
    if (contractId) {
      await fetchContract(contractId)
    }
    setSigningModalOpen(false)
  }

  // Utility functions for date formatting
  const pad = (n: number) => String(n).padStart(2, '0')
  const formatDateKey = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  // Format date as YYYY-MM-DD using local date (not UTC) to avoid timezone issues
  const formatDateForAPI = (d: Date) => {
    const year = d.getFullYear()
    const month = pad(d.getMonth() + 1)
    const day = pad(d.getDate())
    return `${year}-${month}-${day}`
  }
  const formatDateDisplayLong = (d: Date) =>
    d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })

  const isPastDate = (d: Date) => {
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    )
    const compare = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    return compare < todayOnly
  }

  // Fetch available slots for Add Session dialog
  useEffect(() => {
    if (!selectedDate || !addSessionDialogOpen) {
      setSlotsForDate([])
      return
    }

    const therapistId =
      contractDetails?.therapist_id?._id || contractDetails?.therapist_id
    const serviceId =
      contractDetails?.service_id?._id || contractDetails?.service_id

    if (!therapistId) return

    const formattedDate = formatDateForAPI(selectedDate)

      ; (async () => {
        setLoadingSlots(true)
        try {
          const res = await api.get(
            `/therapist/schedule/available-slots?therapistId=${encodeURIComponent(therapistId)}&date=${formattedDate}&serviceId=${serviceId || ''}`
          )
          const serverData = res?.data?.data ?? []
          const normalized: any[] = Array.isArray(serverData)
            ? serverData
              .map((s: any) => {
                if (!s) return null
                const start = s.startTime ?? s.start_time ?? s.start
                const end = s.endTime ?? s.end_time ?? s.end
                if (!start || !end) return null
                return {
                  _id: s._id,
                  startTime: start,
                  endTime: end,
                }
              })
              .filter(Boolean)
            : []
          setSlotsForDate(normalized)
        } catch (err) {
          console.error('Slot fetch error:', err)
          setSlotsForDate([])
        } finally {
          setLoadingSlots(false)
        }
      })()
  }, [selectedDate, contractDetails, addSessionDialogOpen])

  // Fetch available slots for Reschedule dialog
  useEffect(() => {
    if (!rescheduleSelectedDate || !rescheduleDialogOpen) {
      setRescheduleSlotsForDate([])
      return
    }

    const therapistId =
      contractDetails?.therapist_id?._id || contractDetails?.therapist_id
    const serviceId =
      contractDetails?.service_id?._id || contractDetails?.service_id

    if (!therapistId) return

    const formattedDate = formatDateForAPI(rescheduleSelectedDate)

      ; (async () => {
        setLoadingRescheduleSlots(true)
        try {
          const res = await api.get(
            `/therapist/schedule/available-slots?therapistId=${encodeURIComponent(therapistId)}&date=${formattedDate}&serviceId=${serviceId || ''}`
          )
          const serverData = res?.data?.data ?? []
          const normalized: any[] = Array.isArray(serverData)
            ? serverData
              .map((s: any) => {
                if (!s) return null
                const start = s.startTime ?? s.start_time ?? s.start
                const end = s.endTime ?? s.end_time ?? s.end
                if (!start || !end) return null
                return {
                  _id: s._id,
                  startTime: start,
                  endTime: end,
                }
              })
              .filter(Boolean)
            : []
          setRescheduleSlotsForDate(normalized)
        } catch (err) {
          console.error('Reschedule slot fetch error:', err)
          setRescheduleSlotsForDate([])
        } finally {
          setLoadingRescheduleSlots(false)
        }
      })()
  }, [rescheduleSelectedDate, contractDetails, rescheduleDialogOpen])

  // Show snackbar
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info' = 'success'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }))
  }

  // Check if contract is cancelled
  const isCancelledContract = contractDetails?.status === 'cancelled'
  const isCompletedContract = contractDetails?.status === 'completed'
  const isPendingContract = contractDetails?.status === 'pending'

  // Handle Add New Session
  const handleAddNewSession = () => {
    if (isCancelledContract) return
    setSelectedDate(today)
    setSelectedSlots({})
    setPaymentMethod('out_of_pocket')
    setAddSessionDialogOpen(true)
  }

  // Handle Message Provider
  const handleMessageProvider = () => {
    if (!contractDetails?.therapist_id) {
      showSnackbar(
        'Unable to start chat. Provider information is missing.',
        'error'
      )
      return
    }

    const therapistId =
      contractDetails.therapist_id._id || contractDetails.therapist_id
    const patientId = authUser?.id

    if (!therapistId || !patientId) {
      showSnackbar(
        'Unable to start chat. Missing participant information.',
        'error'
      )
      return
    }

    const therapistName =
      `${contractDetails.therapist_id.firstName || ''
        } ${contractDetails.therapist_id.lastName || ''}`.trim() || 'Provider'
    const therapistAvatar = contractDetails.therapist_id.profilePhotoUrl

    const patientName = authUser?.firstName
      ? `${authUser.firstName || ''} ${authUser.lastName || ''}`.trim()
      : authUser?.email || 'Patient'

    setChatProvider({
      patientId,
      therapistId,
      patientDisplayName: patientName,
      therapistDisplayName: therapistName,
      patientAvatarUrl: authUser?.profilePhotoUrl,
      therapistAvatarUrl: therapistAvatar,
    })

    setChatDialogOpen(true)
  }

  const handleSelectSlot = (date: Date, slot: any) => {
    const dateKey = formatDateKey(date)
    const start = slot.startTime
    const end = slot.endTime

    // Toggle slot selection
    if (
      selectedSlots[dateKey] &&
      selectedSlots[dateKey].startTime === start &&
      selectedSlots[dateKey].endTime === end
    ) {
      const copy = { ...selectedSlots }
      delete copy[dateKey]
      setSelectedSlots(copy)
      return
    }

    // Check max 5 sessions limit
    const selectedCount = Object.keys(selectedSlots).length
    if (!selectedSlots[dateKey] && selectedCount >= 5) {
      showSnackbar(
        'You can add up to 5 sessions at a time. Remove one to add another.',
        'warning'
      )
      return
    }

    setSelectedSlots(prev => ({
      ...prev,
      [dateKey]: { slotId: slot._id, startTime: start, endTime: end },
    }))
  }

  const removeSelectedSlot = (dateKey: string) => {
    const copy = { ...selectedSlots }
    delete copy[dateKey]
    setSelectedSlots(copy)
  }

  const handleAddSessionSubmit = async () => {
    if (isCancelledContract) {
      showSnackbar('Cannot add sessions to a cancelled contract', 'error')
      return
    }
    const selectedCount = Object.keys(selectedSlots).length
    if (selectedCount === 0) {
      showSnackbar('Please select at least one session slot', 'error')
      return
    }
    if (!paymentMethod) {
      showSnackbar('Please select a payment method', 'error')
      return
    }

    try {
      setIsSubmitting(true)

      // Convert selectedSlots to bookings array format
      const bookings = Object.entries(selectedSlots).map(([dateKey, slot]) => {
        const parts = dateKey.split('-').map(Number)
        const dateObj = new Date(parts[0], parts[1] - 1, parts[2])
        const formattedDate = formatDateForAPI(dateObj)
        const sessionSlot = `${slot.startTime}-${slot.endTime}`

        return {
          date: formattedDate,
          slot: sessionSlot,
        }
      })

      const payload = {
        contract_id: contractId,
        bookings: bookings,
        payment_method: paymentMethod,
      }

      const res = await api.post('/contracts/session-create', payload)
      const successMessage =
        res?.data?.message ||
        `${selectedCount} session${selectedCount > 1 ? 's' : ''} added successfully!`
      showSnackbar(successMessage, 'success')
      setAddSessionDialogOpen(false)
      setSelectedSlots({})
      setSelectedDate(today)
      setPaymentMethod('')

      // Refresh contract data
      if (contractId) {
        await fetchContract(contractId)
      }
    } catch (error: any) {
      console.error('Error adding sessions:', error)
      const errorMessage =
        error.response?.data?.message || 'Failed to add sessions. Please try again.'
      showSnackbar(errorMessage, 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle Reschedule
  const handleReschedule = (session: any) => {
    if (isCancelledContract) return
    setSelectedSessionForAction(session)
    setRescheduleSelectedDate(today)
    setSelectedRescheduleSlot(null)
    setRescheduleDialogOpen(true)
  }

  const handleRescheduleSubmit = async () => {
    if (!rescheduleSelectedDate || !selectedRescheduleSlot) {
      showSnackbar('Please select a date and time slot', 'error')
      return
    }

    if (!selectedSessionForAction?.id) {
      showSnackbar('Session ID is missing', 'error')
      return
    }

    try {
      setIsSubmitting(true)
      const formattedDate = formatDateForAPI(rescheduleSelectedDate)
      const newSlot = `${selectedRescheduleSlot.startTime}-${selectedRescheduleSlot.endTime}`

      const payload = {
        session_id: selectedSessionForAction.id,
        new_date: formattedDate,
        new_slot: newSlot,
      }

      await api.post('/contracts/session-reschedule', payload)
      showSnackbar('Session rescheduled successfully!', 'success')
      setRescheduleDialogOpen(false)
      setSelectedRescheduleSlot(null)
      setRescheduleSelectedDate(today)
      setSelectedSessionForAction(null)

      // Refresh contract data
      if (contractId) {
        await fetchContract(contractId)
      }
    } catch (error: any) {
      console.error('Error rescheduling session:', error)
      showSnackbar(
        error.response?.data?.message ||
        'Failed to reschedule session. Please try again.',
        'error'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle Cancel
  const handleCancel = (session: any) => {
    if (isCancelledContract) return
    setSelectedSessionForAction(session)
    setCancelReason('')
    setCancelDialogOpen(true)
  }

  const handleCancelSubmit = async () => {
    if (isCancelledContract) {
      showSnackbar('Cannot cancel sessions for a cancelled contract', 'error')
      return
    }
    if (!cancelReason.trim()) {
      showSnackbar('Please provide a reason for cancellation', 'error')
      return
    }

    if (!selectedSessionForAction?.id) {
      showSnackbar('Session ID is missing', 'error')
      return
    }

    try {
      setIsSubmitting(true)
      const payload = {
        session_id: selectedSessionForAction.id,
        reason: cancelReason,
      }

      await api.post('/contracts/session-cancel', payload)
      showSnackbar('Session cancelled successfully!', 'success')
      setCancelDialogOpen(false)
      setCancelReason('')
      setSelectedSessionForAction(null)

      // Refresh contract data
      if (contractId) {
        await fetchContract(contractId)
      }
    } catch (error: any) {
      console.error('Error cancelling session:', error)
      showSnackbar(
        error.response?.data?.message ||
        'Failed to cancel session. Please try again.',
        'error'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // Fetch notes for a session
  const fetchSessionNotes = async (sessionId: string, contractId: string) => {
    if (!sessionId || !contractId) return

    const sessionKey = `${contractId}-${sessionId}`

    // If notes already loaded, just toggle dropdown
    if (sessionNotes[sessionKey]) {
      setOpenNotesDropdown(openNotesDropdown === sessionKey ? null : sessionKey)
      return
    }

    // Fetch notes
    setLoadingNotes(prev => ({ ...prev, [sessionKey]: true }))
    try {
      const notes = await notesApi.getNotesByContractAndSession(
        contractId,
        sessionId
      )
      // Filter notes: only finalized and visible to patient
      const filteredNotes = notes.filter(
        note => note.visibleto_patient === true && note.status === 'finalized'
      )
      setSessionNotes(prev => ({ ...prev, [sessionKey]: filteredNotes }))
      setOpenNotesDropdown(sessionKey)
    } catch (error) {
      console.error('Error fetching notes:', error)
      setSessionNotes(prev => ({ ...prev, [sessionKey]: [] }))
      setOpenNotesDropdown(sessionKey)
    } finally {
      setLoadingNotes(prev => ({ ...prev, [sessionKey]: false }))
    }
  }

  // Handle note PDF download
  const handleNoteDownload = (pdfUrl: string) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank')
    }
  }

  // Helper function to format payment date
  const formatPaymentDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  // Helper function to get invoice status config
  const getInvoiceStatusConfig = (status: string) => {
    switch (status) {
      case 'Paid':
        return {
          color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
          label: 'Paid',
        }
      case 'Pending':
        return {
          color: 'bg-yellow-100 text-yellow-700 border-yellow-300',
          label: 'Pending',
        }
      case 'Draft':
        return {
          color: 'bg-gray-100 text-gray-700 border-gray-300',
          label: 'Draft',
        }
      default:
        return {
          color: 'bg-gray-100 text-gray-700 border-gray-300',
          label: status,
        }
    }
  }

  // Helper function to get claim status config
  const getClaimStatusConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return {
          color: 'bg-yellow-100 text-yellow-700 border-yellow-300',
          label: 'Pending',
        }
      case 'submitted':
        return {
          color: 'bg-blue-100 text-blue-700 border-blue-300',
          label: 'Submitted',
        }
      case 'accepted':
        return {
          color: 'bg-indigo-100 text-indigo-700 border-indigo-300',
          label: 'Accepted',
        }
      case 'rejected':
        return {
          color: 'bg-red-100 text-red-700 border-red-300',
          label: 'Rejected',
        }
      case 'paid':
        return {
          color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
          label: 'Paid',
        }
      default:
        return {
          color: 'bg-gray-100 text-gray-700 border-gray-300',
          label: capitalize(status),
        }
    }
  }

  const mappedSessions =
    sessionDetails?.map(s => {
      const sessionDate = s.session_date
      const sessionTime = s.session_slot || ''
      const dateStr = sessionDate ? formatPaymentDate(sessionDate) : ''

      return {
        id: s._id || '',
        date: dateStr,
        time: sessionTime,
        status: s.status === 'scheduled' ? 'Scheduled' : s.status,
        price: s.session_amount,
        location: `${s.patient_id?.city || ''}, ${s.patient_id?.state || ''}`,
        services: s.service_id?.name || '--',
        paymentMethod: s.payment_method || '',
        cpt_code: s.cpt_code || [],
        icd_code: s.icd_code || [],
        claimedAmount: s.claimed_amount ?? 0,
        approvedAmount: s.approved_amount ?? 0,
        coPay: s.co_pay ?? 0,
        invoiceId: s.invoiceId || null,
        claimId: s.claimId || null,
        claimStatus: s.payer_active ?? 'Active',
        sessionId: s.session_ids,
        contractId: s.contract_ids,
      }
    }) || []

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #6EC39B;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #10b981;
          cursor: pointer;
        }
      `}</style>
      <div className="p-4 mx-auto overflow-x-hidden max-w-7xl sm:p-0">
        {/* Back Button */}
        <button
          onClick={e => navigate('/contracts')}
          className="flex items-center mb-4 text-gray-600 transition-colors cursor-pointer hover:text-green-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Contracts
        </button>

        {/* Cancelled Contract Banner */}
        {isCancelledContract && (
          <div className="mb-4 transition-all duration-400">
            <div className="flex items-start flex-1 gap-3 p-4 border border-red-200 rounded-lg shadow-sm bg-red-50">
              <X className="w-5 h-5 mt-0.5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="mb-1 text-base font-semibold text-red-900">
                  Contract Terminated
                </h3>
                <p className="text-sm text-red-800">
                  This contract has been cancelled and is no longer active. All
                  actions including adding sessions, rescheduling sessions,
                  cancelling sessions have been disabled.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* E-Signature Alert for Pending Contracts */}
        {contractDetails?.hasPatientSigned === false &&
          !isCancelledContract && (
            <div className="mb-4 transition-all duration-400">
              <div className="flex items-center justify-between p-4 border border-yellow-200 rounded-lg shadow-sm bg-yellow-50">
                <div className="flex items-start flex-1 gap-3">
                  <Info className="w-5 h-5 mt-0.5 text-yellow-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="mb-1 text-base font-semibold text-yellow-900">
                      Contract Awaiting Your Signature
                    </h3>
                    <p className="text-sm text-yellow-800">
                      Please review the contract details and scheduled sessions
                      below. Once you're comfortable with the schedule and
                      treatment plan, sign the contract to begin services.
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <button
                    onClick={handleESignClick}
                    disabled={signingLoading}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-yellow-900 transition-colors bg-white border border-yellow-300 rounded-lg cursor-pointer hover:bg-yellow-100 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <PenTool className="w-4 h-4" />
                    {signingLoading ? 'Loading...' : 'E-Sign Contract'}
                  </button>
                </div>
              </div>
            </div>
          )}

        <div className="grid grid-cols-1 gap-4 overflow-x-hidden lg:grid-cols-3 sm:gap-3">
          {/* Left Section - Main Content */}
          <div className="min-w-0 space-y-6 lg:col-span-2">
            {/* Header Card */}
            <div className="p-4 text-white rounded-lg shadow-md bg-emerald-600 sm:p-6">
              <div className="flex flex-col items-start justify-between gap-4 mb-4 sm:flex-row">
                <div className="flex-1">
                  <h1 className="mb-1 text-xl font-bold sm:text-2xl">
                    {contractDetails?.contract_title || contractDetails?.service_id?.name}
                  </h1>
                  <div className="flex items-center text-xs sm:text-sm">
                    <span className="mr-2">
                      Contract ID: {contractDetails?.contract_ids}
                    </span>
                    <Download
                      onClick={e =>
                        downloadContract(contractDetails?.envelope_id)
                      }
                      className="w-4 h-4 transition-transform cursor-pointer hover:scale-110"
                    />
                  </div>
                </div>
                {/* {!hasNoSessions && (
                    <>
                      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Total Amount: ${(contract.totalAmount ?? 0).toFixed(2)}
                      </Typography>
                    </>
                  )} */}
                <div className="text-left sm:text-right">
                  <div className="mb-1 text-sm">
                    Per Session: ${contractDetails?.contract_amount?.toFixed(2)}
                  </div>
                  {/* if any session then only total amount show */}
                  {contractDetails?.total_session > 0 && (
                    <div className="mb-1 text-sm">
                      Total Amount:${contractDetails?.total_amount?.toFixed(2)}
                    </div>
                  )}
                  <span className="px-3 py-1 text-xs font-semibold bg-white rounded-full text-emerald-600">
                    {toCaps(contractDetails?.status)}
                  </span>
                </div>

              </div>

              <div className="flex flex-col items-start gap-4 mb-6 sm:flex-row sm:items-center">
                <div className="flex items-center flex-1">
                  <img
                    src={contractDetails?.therapist_id?.profilePhotoUrl || ''}
                    alt="Dr. Sarah Johnson"
                    className="w-10 h-10 mr-3 bg-white rounded-full sm:w-12 sm:h-12"
                  />
                  <div>
                    <div className="text-sm font-semibold sm:text-base">
                      {contractDetails?.therapist_id?.firstName}{' '}
                      {contractDetails?.therapist_id?.lastName}
                    </div>
                    <div className="text-xs sm:text-sm text-emerald-100">
                      Provider
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 text-xs sm:gap-8 sm:text-sm">
                  <div>
                    <div className="mb-1 text-emerald-100">Start date</div>
                    <div className="font-semibold">
                      {contractDetails?.start_date &&
                        dayjs(contractDetails?.start_date).isValid()
                        ? dayjs(contractDetails?.start_date).format(
                          'MM/DD/YYYY'
                        )
                        : '--'}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 text-emerald-100">End date</div>
                    <div className="font-semibold">
                      {contractDetails?.end_date &&
                        dayjs(contractDetails?.end_date).isValid()
                        ? dayjs(contractDetails?.end_date).format('MM/DD/YYYY')
                        : '--'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm">Session Progress</span>
                  <span className="text-xs font-semibold sm:text-sm">
                    {contractDetails?.completed_session} of{' '}
                    {contractDetails?.total_session}
                  </span>
                </div>
                <div className="w-full h-2 mb-2 rounded-full bg-emerald-500">
                  <div
                    className="h-2 transition-all duration-500 bg-white rounded-full"
                    style={{
                      width: `${(contractDetails?.completed_session / contractDetails?.total_session) * 100}%`,
                    }}
                  ></div>
                </div>
                <div className="text-xs sm:text-sm">
                  Next date:{' '}
                  <span className="font-semibold">
                    {contractDetails?.next_date &&
                      dayjs(contractDetails?.next_date).isValid()
                      ? dayjs(contractDetails?.next_date).format('MM/DD/YYYY')
                      : '--'}
                  </span>
                </div>
              </div>
            </div>

            {/* Insurance Details */}
            {(contractDetails?.insuranceCardId ||
              contractDetails?.policy_id) && (
                <div className="p-4 bg-white rounded-lg shadow-sm sm:p-6">
                  <h2 className="mb-4 text-base font-semibold sm:text-lg">
                    Insurance Details
                  </h2>
                  <div className="p-4 border-l-4 rounded-r bg-emerald-50 border-emerald-600">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <div className="mb-1 text-xs text-gray-500 sm:text-sm">
                          Insurance Provider:
                        </div>
                        <div className="text-sm font-semibold text-emerald-600 sm:text-base">
                          {contractDetails?.insuranceCardId?.insuranceName}
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 text-xs text-gray-500 sm:text-sm">
                          Member ID:
                        </div>
                        <div className="text-sm font-semibold sm:text-base">
                          {contractDetails?.policy_id}
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 text-xs text-gray-500 sm:text-sm">
                          Plan Type:
                        </div>
                        <div className="text-sm font-semibold text-emerald-600 sm:text-base">
                          {capitalize(
                            contractDetails?.insuranceCardId?.insuranceType || ''
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Contract Timeline */}
            <ContractTimeline timeline={timeline} loading={timelineLoading} />

            {/* Contract Documents */}
            <ContractDocuments
              documents={contractDocuments}
              loading={contractDocumentsLoading}
              onDownload={handleDownloadContractDocument}
            />

            {/* Documents */}
            <DocumentsSection
              contractId={contractId || ''}
              documents={documents}
              loading={documentsLoading}
              onUpload={handleUploadDocument}
            />
          </div>

          {/* Right Section - Sessions */}
          <div className="w-full min-w-0 lg:col-span-1 lg:max-w-full">
            <div className="p-4 overflow-hidden bg-white rounded-lg shadow-sm lg:sticky lg:top-6">
              <div className="flex flex-col items-start justify-between gap-2 mb-4">
                <h2 className="flex-shrink-0 text-base font-semibold sm:text-lg">
                  {activeTab}
                </h2>
                <div className="flex flex-wrap items-center w-full gap-2">
                  <button
                    onClick={handleMessageProvider}
                    className="flex items-center px-3 py-2 text-xs font-medium text-gray-700 transition-colors bg-white border border-gray-300 rounded-lg shadow-sm cursor-pointer sm:text-sm hover:bg-gray-50 hover:shadow-md"
                  >
                    <Chat fontSize="small" className="w-3 h-3 mr-1" />
                    Message Provider
                  </button>
                  {!isCompletedContract && (
                    <button
                      onClick={handleAddNewSession}
                      disabled={isCancelledContract || isPendingContract}
                      className="flex items-center px-3 py-2 text-xs font-medium text-white transition-colors rounded-lg shadow-sm cursor-pointer bg-emerald-600 sm:text-sm hover:bg-emerald-700 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600 disabled:hover:shadow-sm"
                      title={
                        isCancelledContract
                          ? 'Contract is cancelled. Actions are disabled.'
                          : isPendingContract
                            ? 'Contract is pending. Actions are disabled.'
                            : ''
                      }
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Session
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-4 max-h-[600px] lg:max-h-[calc(100vh-240px)] overflow-y-auto overflow-x-hidden pr-2 custom-scrollbar">
                {mappedSessions?.map((session: any) => (
                  <div
                    key={session?.id}
                    className="p-3 transition-shadow border border-gray-300 rounded-lg sm:p-4 hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="mb-1 text-xs font-semibold break-words sm:text-sm">
                          {session?.date}
                        </div>
                        <div className="text-xs text-gray-500 break-words">
                          {session?.time}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center flex-shrink-0 gap-2">
                        {/* Payment Method Badge */}
                        {session?.paymentMethod && (
                          <div
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${session.paymentMethod === 'insurance'
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : session.paymentMethod === 'out_of_pocket'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-gray-50 text-gray-700 border border-gray-200'
                              }`}
                          >
                            {session.paymentMethod === 'insurance' ? (
                              <Building2 className="flex-shrink-0 w-3 h-3" />
                            ) : session.paymentMethod === 'out_of_pocket' ? (
                              <CreditCard className="flex-shrink-0 w-3 h-3" />
                            ) : null}
                            <span className="truncate">
                              {session.paymentMethod === 'insurance'
                                ? 'Insurance'
                                : session.paymentMethod === 'out_of_pocket'
                                  ? 'Out of Pocket'
                                  : capitalize(session.paymentMethod)}
                            </span>
                          </div>
                        )}
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${session.status === 'Completed'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-yellow-100 text-yellow-700'
                            }`}
                        >
                          {capitalize(session?.status)}
                        </span>
                      </div>
                    </div>

                    <div className="mb-2 text-xs sm:text-sm">
                      <span className="font-semibold">
                        ${session?.price?.toFixed(2)}
                      </span>{' '}
                      per session
                    </div>

                    <div className="flex items-center min-w-0 mb-3 text-xs sm:text-sm">
                      <MapPin className="flex-shrink-0 w-3 h-3 mr-1 sm:w-4 sm:h-4 text-emerald-500" />
                      <span className="break-words">{session?.location}</span>
                    </div>

                    <div className="p-2 mb-3 rounded bg-gray-50">
                      <div className="text-xs text-gray-600 break-words">
                        Services: {session?.services}
                      </div>
                    </div>

                    <div className="mb-3 text-xs text-gray-500 break-words">
                      <div className="mb-1">
                        <span className="font-medium">Contract ID:</span>{' '}
                        <span className="font-mono break-all">
                          {session?.contractId || contractId || '--'}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Session ID:</span>{' '}
                        <span className="font-mono break-all">
                          {session?.sessionId || session?.id || '--'}
                        </span>
                      </div>
                    </div>

                    {/* View Notes Dropdown */}
                    <div className="relative mb-3">
                      <button
                        onClick={() =>
                          fetchSessionNotes(
                            session?.id, // Use MongoDB _id for session
                            contractId || '' // Use MongoDB contractId from URL params
                          )
                        }
                        className="flex items-center justify-between w-full px-3 py-2 text-xs font-medium transition-colors border border-gray-300 rounded-lg cursor-pointer text-emerald-600 hover:bg-emerald-50 hover:border-emerald-400"
                      >
                        <div className="flex items-center">
                          <StickyNote className="w-4 h-4 mr-1" />
                          View Notes
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${openNotesDropdown ===
                            `${contractId || ''}-${session?.id}`
                            ? 'rotate-180'
                            : ''
                            }`}
                        />
                      </button>

                      {openNotesDropdown ===
                        `${contractId || ''}-${session?.id}` && (
                          <div className="absolute left-0 right-0 z-10 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                            {loadingNotes[
                              `${contractId || ''}-${session?.id}`
                            ] ? (
                              <div className="flex items-center justify-center p-4">
                                <CircularProgress
                                  size={20}
                                  sx={{ color: '#3BAE78' }}
                                />
                                <span className="ml-2 text-xs text-gray-600">
                                  Loading notes...
                                </span>
                              </div>
                            ) : sessionNotes[`${contractId || ''}-${session?.id}`]
                              ?.length > 0 ? (
                              <div className="overflow-y-auto max-h-48">
                                {sessionNotes[
                                  `${contractId || ''}-${session?.id}`
                                ]?.map(note => (
                                  <button
                                    key={note._id}
                                    onClick={() =>
                                      handleNoteDownload(note.pdf_url || '')
                                    }
                                    className="w-full px-4 py-2 text-xs text-left text-gray-700 transition-colors border-b border-gray-100 hover:bg-emerald-50 hover:text-emerald-700 last:border-b-0"
                                  >
                                    {note.noteCategory?.title || 'Untitled Note'}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="px-4 py-3 text-xs text-center text-gray-500">
                                No notes available
                              </div>
                            )}
                          </div>
                        )}
                    </div>

                    {/* Payment Information - Out of Pocket Sessions with Invoice */}
                    {session?.paymentMethod === 'out_of_pocket' &&
                      session?.invoiceId &&
                      typeof session.invoiceId === 'object' &&
                      session.invoiceId.invoice_ids && (
                        <div className="p-3 mt-3 overflow-hidden border rounded-lg shadow-sm bg-gradient-to-r from-emerald-50 to-white border-emerald-200">
                          <div className="space-y-2">
                            {/* First Line: Invoice ID */}
                            <div className="flex items-center min-w-0 gap-2">
                              <span className="flex-shrink-0 text-xs font-medium text-gray-600">
                                Invoice ID:
                              </span>
                              <button
                                onClick={() =>
                                  navigate('/invoices', {
                                    state: {
                                      searchQuery:
                                        session.invoiceId.invoice_ids || '',
                                    },
                                  })
                                }
                                className="flex items-center min-w-0 gap-1 text-xs font-semibold transition-colors cursor-pointer text-emerald-600 hover:text-emerald-700 hover:underline"
                              >
                                <span className="truncate">
                                  {session.invoiceId.invoice_ids || 'N/A'}
                                </span>
                                <ExternalLink className="flex-shrink-0 w-3 h-3" />
                              </button>
                            </div>

                            {/* Second Line: Payment Date and Status */}
                            <div className="flex flex-wrap items-center gap-3">
                              {/* Payment Date */}
                              {session.invoiceId.payment_date && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-gray-600">
                                    Payment Date:
                                  </span>
                                  <span className="text-xs font-semibold text-gray-800">
                                    {formatPaymentDate(
                                      session.invoiceId.payment_date
                                    )}
                                  </span>
                                </div>
                              )}

                              {/* Status */}
                              {session.invoiceId.status && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-gray-600">
                                    Status:
                                  </span>
                                  <span
                                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getInvoiceStatusConfig(session.invoiceId.status || 'Pending').color}`}
                                  >
                                    {
                                      getInvoiceStatusConfig(
                                        session.invoiceId.status || 'Pending'
                                      ).label
                                    }
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                    {/* Claim Information - Insurance Sessions with Claim */}
                    {session?.paymentMethod === 'insurance' &&
                      session?.claimId &&
                      typeof session.claimId === 'object' &&
                      (session.claimId.claimId || session.claimId._id) && (
                        <div className="p-3 mt-3 overflow-hidden border border-blue-200 rounded-lg shadow-sm bg-gradient-to-r from-blue-50 to-white">
                          <div className="space-y-2">
                            {/* First Line: Claim ID */}
                            <div className="flex items-center min-w-0 gap-2">
                              <span className="flex-shrink-0 text-xs font-medium text-gray-600">
                                Claim ID:
                              </span>
                              <button
                                onClick={() =>
                                  navigate('/claims', {
                                    state: {
                                      searchQuery:
                                        session.claimId.claimId ||
                                        session.claimId._id ||
                                        '',
                                    },
                                  })
                                }
                                className="flex items-center min-w-0 gap-1 text-xs font-semibold transition-colors cursor-pointer text-emerald-600 hover:text-emerald-700 hover:underline"
                              >
                                <span className="truncate">
                                  {session.claimId.claimId ||
                                    session.claimId._id ||
                                    'N/A'}
                                </span>
                                <ExternalLink className="flex-shrink-0 w-3 h-3" />
                              </button>
                            </div>

                            {/* Second Line: Submission Date and Status */}
                            <div className="flex flex-wrap items-center gap-3">
                              {/* Submission Date */}
                              {(session.claimId.submissionDate ||
                                session.claimId.submission_date) && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-600">
                                      Submission Date:
                                    </span>
                                    <span className="text-xs font-semibold text-gray-800">
                                      {formatPaymentDate(
                                        session.claimId.submissionDate ||
                                        session.claimId.submission_date
                                      )}
                                    </span>
                                  </div>
                                )}

                              {/* Status */}
                              {session.claimId.status && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-gray-600">
                                    Status:
                                  </span>
                                  <span
                                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getClaimStatusConfig(session.claimId.status || 'pending').color}`}
                                  >
                                    {
                                      getClaimStatusConfig(
                                        session.claimId.status || 'pending'
                                      ).label
                                    }
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                    {session?.status === 'Completed' &&
                      session?.paymentMethod !== 'out_of_pocket' &&
                      session?.paymentMethod !== 'insurance' && (
                        <>
                          <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                            <div>
                              <div className="mb-1 text-gray-500">
                                Payer Active
                              </div>
                              <div className="font-semibold">
                                ${session?.claimedAmount.toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <div className="mb-1 text-gray-500">
                                Claimed Amount
                              </div>
                              <div className="font-semibold">
                                ${session?.approvedAmount.toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <div className="mb-1 text-gray-500">
                                Approved Amount
                              </div>
                              <div className="font-semibold">
                                ${session?.coPay.toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <div className="mb-1 text-gray-500">Co Pay</div>
                              <div className="font-semibold text-emerald-600">
                                ${session?.coPay.toFixed(2)}
                              </div>
                            </div>
                          </div>

                          <div className="mb-2 text-xs">
                            <span className="text-gray-500">Invoice #:</span>
                            <span className="ml-1 font-semibold">
                              {typeof session?.invoiceId === 'string'
                                ? session.invoiceId
                                : '--'}
                            </span>
                          </div>

                          <div className="flex items-center">
                            <span className="text-xs">
                              Claim Status:{' '}
                              <span className="font-semibold text-emerald-600">
                                ✓ {session?.claimStatus}
                              </span>
                            </span>
                          </div>
                        </>
                      )}

                    {(session?.status === 'Scheduled' ||
                      session?.status === 'rescheduled') &&
                      session?.status !== 'completed' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleReschedule(session)}
                            disabled={isCancelledContract}
                            className="flex-1 px-3 py-2 text-xs font-medium text-white transition-colors rounded cursor-pointer bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600"
                            title={
                              isCancelledContract
                                ? 'Contract is cancelled. Actions are disabled.'
                                : ''
                            }
                          >
                            Reschedule
                          </button>
                          <button
                            onClick={() => handleCancel(session)}
                            disabled={isCancelledContract}
                            className="flex items-center justify-center px-3 py-2 text-xs text-red-600 transition-colors border border-red-300 rounded cursor-pointer hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:border-red-200 disabled:text-red-400"
                            title={
                              isCancelledContract
                                ? 'Contract is cancelled. Actions are disabled.'
                                : ''
                            }
                          >
                            <X className="w-3 h-3 mr-1" />
                            Cancel
                          </button>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DocSign Modal */}
      {contractId && (
        <DocuSignSigner
          open={signingModalOpen}
          onClose={handleCloseSigningModal}
          onSigned={handleSigned}
          contractId={contractId}
          fromBooking={false}
        />
      )}

      {/* Add New Session Dialog */}
      <Dialog
        open={addSessionDialogOpen}
        onClose={() => !isSubmitting && setAddSessionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Session</DialogTitle>
        <DialogContent dividers>
          <div className="mt-4 space-y-6">
            {/* Calendar and Time Slots Side by Side */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Calendar */}
              <div className="p-4 bg-white border border-gray-100 rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setMonthOffset(m => m - 1)}
                      className="p-2 transition-colors rounded-full hover:bg-gray-100"
                    >
                      <FiChevronLeft />
                    </button>
                    <div className="text-sm font-medium">
                      {monthStart.toLocaleString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </div>
                    <button
                      onClick={() => setMonthOffset(m => m + 1)}
                      className="p-2 transition-colors rounded-full hover:bg-gray-100"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">Select a day *</div>
                </div>

                <div className="grid grid-cols-7 gap-1.5 mb-3 text-xs text-center text-gray-500 font-medium">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <div key={d} className="py-1">
                      {d}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1.5">
                  {monthDates.map((d, index) => {
                    if (d === null) {
                      return (
                        <div
                          key={`empty-${index}`}
                          className="aspect-square"
                          aria-hidden="true"
                        />
                      )
                    }

                    const key = formatDateKey(d)
                    const disabled = isPastDate(d)
                    const isActive =
                      selectedDate && formatDateKey(selectedDate) === key
                    const dateSelected = Boolean(selectedSlots[key])

                    return (
                      <button
                        key={key}
                        onClick={() => !disabled && setSelectedDate(d)}
                        disabled={disabled}
                        className={`relative aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all ${disabled
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                          : dateSelected
                            ? 'bg-emerald-50 text-emerald-700 border-2 border-emerald-500 font-semibold'
                            : isActive
                              ? 'bg-[#3BAE78] text-white shadow-md scale-105'
                              : 'hover:bg-gray-50 bg-white text-gray-700 border border-gray-200 hover:border-[#3BAE78] cursor-pointer'
                          }`}
                      >
                        {d.getDate()}
                        {dateSelected && (
                          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Time Slots */}
              <div className="p-4 bg-white border border-gray-200 shadow-sm rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold tracking-wide text-gray-800">
                    Select slot time *
                  </div>
                  {selectedDate && (
                    <div className="px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded-md">
                      {formatDateDisplayLong(selectedDate)}
                    </div>
                  )}
                </div>

                {!selectedDate ? (
                  <div className="py-12 text-center">
                    <div className="mb-2 text-sm text-gray-400">
                      Select a date first
                    </div>
                    <div className="text-xs text-gray-300">
                      Choose a date from the calendar to view available time
                      slots
                    </div>
                  </div>
                ) : loadingSlots ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <CircularProgress size={24} sx={{ color: '#3BAE78' }} />
                    <div className="mt-3 text-xs text-gray-500">
                      Loading slots...
                    </div>
                  </div>
                ) : slotsForDate.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="mb-1 text-sm font-medium text-gray-600">
                      No slots available
                    </div>
                    <div className="text-xs text-gray-400">
                      Try selecting a different date
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                    {slotsForDate.map(slot => {
                      const label = `${slot.startTime} - ${slot.endTime}`
                      const dateKey = selectedDate
                        ? formatDateKey(selectedDate)
                        : ''
                      const isSelected =
                        selectedSlots[dateKey] &&
                        selectedSlots[dateKey].startTime === slot.startTime &&
                        selectedSlots[dateKey].endTime === slot.endTime
                      const isFirstSessionDisabled = contractDetails?.total_session === 0 && Object.keys(selectedSlots).length > 0

                      return (
                        <button
                          key={slot._id ?? label}
                          onClick={() => {
                            if (isFirstSessionDisabled) {
                              showSnackbar("For the first session, you can select only one date.", "error")
                              return;
                            }
                            selectedDate && handleSelectSlot(selectedDate, slot)
                          }}
                          className={`w-full text-left rounded-xl px-4 py-3 transition-all border-2 ${isSelected
                            ? 'bg-[#3BAE78] border-[#3BAE78] text-white shadow-md'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-300 cursor-pointer'
                            }`}
                        >
                          <div className="text-sm font-medium leading-snug">
                            {label}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Sessions List */}
            <div className="pt-4">
              <div className="p-4 border border-gray-200 bg-gray-50 rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold text-gray-800">
                    Selected Sessions
                  </div>
                  {contractDetails?.total_session !== 0 && (
                    <div className="text-xs text-gray-500">
                      {Object.keys(selectedSlots).length} / 5 selected
                    </div>
                  )}
                </div>

                {Object.keys(selectedSlots).length === 0 ? (
                  <div className="py-4 text-sm text-center text-gray-500">
                    No sessions selected yet. Select dates and time slots above.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1 custom-scrollbar">
                    {Object.entries(selectedSlots)
                      .sort(([dateKeyA], [dateKeyB]) =>
                        dateKeyA.localeCompare(dateKeyB)
                      )
                      .map(([dateKey, slot]) => {
                        const parts = dateKey.split('-').map(Number)
                        const dateObj = new Date(
                          parts[0],
                          parts[1] - 1,
                          parts[2]
                        )
                        const dayLabel = formatDateDisplayLong(dateObj)

                        return (
                          <div
                            key={dateKey}
                            className="flex items-center justify-between p-3 bg-white border rounded-lg border-emerald-200"
                          >
                            <div>
                              <div className="text-sm font-semibold text-gray-800">
                                {dayLabel}
                              </div>
                              <div className="text-xs text-gray-500">
                                {slot.startTime} – {slot.endTime}
                              </div>
                            </div>
                            <button
                              onClick={() => removeSelectedSlot(dateKey)}
                              className="p-1 text-gray-400 transition-colors hover:text-red-500"
                              aria-label={`Remove ${dateKey}`}
                            >
                              <FiX size={18} />
                            </button>
                          </div>
                        )
                      })}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="pt-4">
              <div className="p-4 bg-white border border-gray-200 rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold text-gray-800">
                    Payment Method <span className="text-red-500">*</span>
                  </div>
                </div>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <ToggleButtonGroup
                    value={paymentMethod}
                    exclusive
                    onChange={(_, value) => {
                      const hasNoSessions = contractDetails?.total_session === 0;
                      if (hasNoSessions && value === "insurance") {
                        showSnackbar("Insurance billing is not allowed for the first session. Please select self-pay.", "error")
                        return;
                      }
                      if (value !== null) {
                        setPaymentMethod(value)
                      }
                    }}
                    aria-label="payment method"
                    fullWidth
                    sx={{
                      gap: 2,
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        py: 1.5,
                        px: 3,
                        border: '2px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        fontWeight: 500,
                        '&.Mui-selected': {
                          backgroundColor: '#3BAE78',
                          color: 'white',
                          borderColor: '#3BAE78',
                          '&:hover': {
                            backgroundColor: '#33a76a',
                            borderColor: '#33a76a',
                          },
                        },
                        '&:hover': {
                          backgroundColor: 'grey.50',

                        },
                      },
                    }}
                  >
                    <ToggleButton
                      value="out_of_pocket"
                      aria-label="out of pocket"
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CreditCard size={18} />
                        <span>Self-Pay (out of pocket)</span>
                      </Box>
                    </ToggleButton>
                    <ToggleButton value="insurance" aria-label="insurance">
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Building2 size={18} />
                        <span>Insurance-Billed</span>
                      </Box>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {!paymentMethod && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Please select a payment method
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddSessionDialogOpen(false)
              setSelectedSlots({})
              setSelectedDate(today)
              setPaymentMethod('')
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSessionSubmit}
            variant="contained"
            disabled={
              isSubmitting ||
              Object.keys(selectedSlots).length === 0 ||
              !paymentMethod
            }
            sx={{
              backgroundColor: '#3BAE78',
              '&:hover': { backgroundColor: '#33a76a' },
            }}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting
              ? 'Adding...'
              : `Add ${Object.keys(selectedSlots).length} Session${Object.keys(selectedSlots).length > 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog
        open={rescheduleDialogOpen}
        onClose={() => !isSubmitting && setRescheduleDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reschedule Session</DialogTitle>
        <DialogContent>
          <div className="mt-4 space-y-6">
            {selectedSessionForAction && (
              <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
                <div className="mb-1 text-sm font-semibold text-amber-900">
                  Current Session
                </div>
                <div className="text-xs text-amber-700">
                  {selectedSessionForAction.date} at{' '}
                  {selectedSessionForAction.time}
                </div>
              </div>
            )}

            {/* Calendar and Time Slots Side by Side */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Calendar */}
              <div className="p-4 bg-white border border-gray-100 rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setMonthOffset(m => m - 1)}
                      className="p-2 transition-colors rounded-full hover:bg-gray-100"
                    >
                      <FiChevronLeft />
                    </button>
                    <div className="text-sm font-medium">
                      {monthStart.toLocaleString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </div>
                    <button
                      onClick={() => setMonthOffset(m => m + 1)}
                      className="p-2 transition-colors rounded-full hover:bg-gray-100"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">Select a day *</div>
                </div>

                <div className="grid grid-cols-7 gap-1.5 mb-3 text-xs text-center text-gray-500 font-medium">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <div key={d} className="py-1">
                      {d}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1.5">
                  {monthDates.map((d, index) => {
                    if (d === null) {
                      return (
                        <div
                          key={`empty-${index}`}
                          className="aspect-square"
                          aria-hidden="true"
                        />
                      )
                    }

                    const key = formatDateKey(d)
                    const disabled = isPastDate(d)
                    const isActive =
                      rescheduleSelectedDate &&
                      formatDateKey(rescheduleSelectedDate) === key

                    return (
                      <button
                        key={key}
                        onClick={() =>
                          !disabled && setRescheduleSelectedDate(d)
                        }
                        disabled={disabled}
                        className={`relative aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all ${disabled
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                          : isActive
                            ? 'bg-[#3BAE78] text-white shadow-md scale-105'
                            : 'hover:bg-gray-50 bg-white text-gray-700 border border-gray-200 hover:border-[#3BAE78] cursor-pointer'
                          }`}
                      >
                        {d.getDate()}
                        {isActive && (
                          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Time Slots */}
              <div className="p-4 bg-white border border-gray-200 shadow-sm rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold tracking-wide text-gray-800">
                    Select new slot time *
                  </div>
                  {rescheduleSelectedDate && (
                    <div className="px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded-md">
                      {formatDateDisplayLong(rescheduleSelectedDate)}
                    </div>
                  )}
                </div>

                {!rescheduleSelectedDate ? (
                  <div className="py-12 text-center">
                    <div className="mb-2 text-sm text-gray-400">
                      Select a date first
                    </div>
                    <div className="text-xs text-gray-300">
                      Choose a date from the calendar to view available time
                      slots
                    </div>
                  </div>
                ) : loadingRescheduleSlots ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <CircularProgress size={24} sx={{ color: '#3BAE78' }} />
                    <div className="mt-3 text-xs text-gray-500">
                      Loading slots...
                    </div>
                  </div>
                ) : rescheduleSlotsForDate.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="mb-1 text-sm font-medium text-gray-600">
                      No slots available
                    </div>
                    <div className="text-xs text-gray-400">
                      Try selecting a different date
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                    {rescheduleSlotsForDate.map(slot => {
                      const label = `${slot.startTime} - ${slot.endTime}`
                      const isSelected =
                        selectedRescheduleSlot &&
                        selectedRescheduleSlot.startTime === slot.startTime &&
                        selectedRescheduleSlot.endTime === slot.endTime
                      const isFirstSessionDisabled = contractDetails?.total_session === 0 && Object.keys(selectedSlots).length > 0
                      return (
                        <button
                          key={slot._id ?? label}
                          onClick={() => {
                            if (isFirstSessionDisabled) {
                              showSnackbar("For the first session, you can select only one date.", "error")
                              return;
                            }
                            setSelectedRescheduleSlot(slot)
                          }}
                          className={`w-full text-left rounded-xl px-4 py-3 transition-all border-2 ${isSelected
                            ? 'bg-[#3BAE78] border-[#3BAE78] text-white shadow-md'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-300 cursor-pointer'
                            }`}
                        >
                          <div className="text-sm font-medium leading-snug">
                            {label}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRescheduleDialogOpen(false)
              setSelectedRescheduleSlot(null)
              setRescheduleSelectedDate(today)
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRescheduleSubmit}
            variant="contained"
            disabled={
              isSubmitting || !rescheduleSelectedDate || !selectedRescheduleSlot
            }
            sx={{
              backgroundColor: '#3BAE78',
              '&:hover': { backgroundColor: '#33a76a' },
            }}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting ? 'Rescheduling...' : 'Reschedule Session'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !isSubmitting && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Session</DialogTitle>
        <DialogContent>
          <div className="mt-4 space-y-4">
            {selectedSessionForAction && (
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-sm font-medium text-gray-700">
                  Session Details
                </div>
                <div className="mt-1 text-xs text-gray-600">
                  {selectedSessionForAction.date} at{' '}
                  {selectedSessionForAction.time}
                </div>
              </div>
            )}

            <TextField
              label="Reason for Cancellation *"
              multiline
              rows={4}
              fullWidth
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Please provide a reason for cancelling this session..."
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={isSubmitting}
          >
            Close
          </Button>
          <Button
            onClick={handleCancelSubmit}
            variant="contained"
            color="error"
            disabled={isSubmitting || !cancelReason.trim()}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting ? 'Cancelling...' : 'Cancel Session'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Chat Dialog */}
      {chatProvider && chatProvider.patientId && chatProvider.therapistId && (
        <ChatDialog
          open={chatDialogOpen}
          onClose={() => {
            setChatDialogOpen(false)
            setChatProvider(null)
          }}
          patientId={chatProvider.patientId}
          therapistId={chatProvider.therapistId}
          patientDisplayName={chatProvider.patientDisplayName}
          therapistDisplayName={chatProvider.therapistDisplayName}
          patientAvatarUrl={chatProvider.patientAvatarUrl}
          therapistAvatarUrl={chatProvider.therapistAvatarUrl}
        />
      )}
    </div>
  )
}

export default ContractDetailsPage
